import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindDisplaySettingsLanguagesQueryResponse,
  FindDisplaySettingsLanguagesPathParams,
} from "../types/FindDisplaySettingsLanguages"

type FindDisplaySettingsLanguagesClient = typeof client<
  FindDisplaySettingsLanguagesQueryResponse,
  Error,
  never
>
type FindDisplaySettingsLanguages = {
  data: FindDisplaySettingsLanguagesQueryResponse
  error: Error
  request: never
  pathParams: FindDisplaySettingsLanguagesPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindDisplaySettingsLanguagesClient>>
  client: {
    parameters: Partial<Parameters<FindDisplaySettingsLanguagesClient>[0]>
    return: Awaited<ReturnType<FindDisplaySettingsLanguagesClient>>
  }
}
export const findDisplaySettingsLanguagesQueryKey = (
  displaySettingsId: FindDisplaySettingsLanguagesPathParams["displaySettingsId"]
) =>
  [
    {
      url: "/v1/display-settings/find-display-settings-languages/:displaySettingsId",
      params: { displaySettingsId },
    },
  ] as const
export type FindDisplaySettingsLanguagesQueryKey = ReturnType<
  typeof findDisplaySettingsLanguagesQueryKey
>
export function findDisplaySettingsLanguagesQueryOptions<
  TData = FindDisplaySettingsLanguages["response"],
  TQueryData = FindDisplaySettingsLanguages["response"],
>(
  displaySettingsId: FindDisplaySettingsLanguagesPathParams["displaySettingsId"],
  options: FindDisplaySettingsLanguages["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindDisplaySettingsLanguages["response"],
    FindDisplaySettingsLanguages["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findDisplaySettingsLanguagesQueryKey(displaySettingsId)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindDisplaySettingsLanguages["data"],
        FindDisplaySettingsLanguages["error"]
      >({
        method: "get",
        url: `/v1/display-settings/find-display-settings-languages/${displaySettingsId}`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find display settings languages
 * @link /v1/display-settings/find-display-settings-languages/:displaySettingsId
 */
export function useFindDisplaySettingsLanguages<
  TData = FindDisplaySettingsLanguages["response"],
  TQueryData = FindDisplaySettingsLanguages["response"],
  TQueryKey extends QueryKey = FindDisplaySettingsLanguagesQueryKey,
>(
  displaySettingsId: FindDisplaySettingsLanguagesPathParams["displaySettingsId"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindDisplaySettingsLanguages["response"],
        FindDisplaySettingsLanguages["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindDisplaySettingsLanguages["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindDisplaySettingsLanguages["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ??
    findDisplaySettingsLanguagesQueryKey(displaySettingsId)
  const query = useQuery<
    FindDisplaySettingsLanguages["data"],
    FindDisplaySettingsLanguages["error"],
    TData,
    any
  >({
    ...findDisplaySettingsLanguagesQueryOptions<TData, TQueryData>(
      displaySettingsId,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindDisplaySettingsLanguages["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
